import { useState, useEffect, useRef } from 'react';
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/20/solid';
import { formatDate, FORMAT } from '../../../../lib/date';
import request from '../../../../lib/request';

const Habit = ({ user }) => {
  const typeDialogRef = useRef(null);
  const updateHabitRef = useRef(null);

  const [habits, setHabits] = useState([]);
  const [newHabit, setNewHabit] = useState(null);
  const [editingHabit, setEditingHabit] = useState(null);

  useEffect(() => {
    if (user.activated || user.programRegistered) {
      getDailyHabits();
    }
  }, []);

  const getDailyHabits = async () => {
    try {
      const { result } = await request.get('/user/habit');

      if (result && result.length > 0) {
        setHabits(
          result.map((habit) => ({
            ...habit,
            remindAt: habit.reminder
              ? formatDate(
                  new Date(`${formatDate(new Date())}T${habit.remindAt}Z`),
                  FORMAT.HOURMIN
                )
              : '',
          }))
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddHabit = () => {
    openTypeDialog();
  };

  const handleDeleteHabit = async (id) => {
    if (
      window.confirm(
        `Are you sure you want to delete\n"${
          habits.find((habit) => habit.id === id).name
        }"?`
      )
    ) {
      try {
        const { result } = await request.delete(`/user/habit/${id}`);
        if (result) {
          setHabits(habits.filter((habit) => habit.id !== id));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const openTypeDialog = () => {
    typeDialogRef.current.showModal();
  };

  const closeTypeDialog = () => {
    typeDialogRef.current.close();
  };

  const openUpdateHabitDialog = (habit) => {
    setEditingHabit(habit);
    updateHabitRef.current.showModal();
  };

  const closeUpdateHabitDialog = () => {
    setEditingHabit(null);
    updateHabitRef.current.close();
  };

  const handleCloseTypeDialog = () => {
    setNewHabit(null);
    closeTypeDialog();
  };

  const handleSelectHabitType = (type) => {
    const habit = {
      name: '',
      completed: false,
      reminder: false,
      remindAt: '00:00',
    };
    if (type === 'COUNT') {
      habit.type = 'COUNT';
      habit.count = 0;
      habit.current = 0;
    } else if (type === 'CHECK') {
      habit.type = 'CHECK';
    }
    setNewHabit(habit);
  };

  const handleNewHabitNameChange = (e) => {
    setNewHabit({ ...newHabit, name: e.target.value });
  };

  const handleNewHabitCountChange = (e) => {
    setNewHabit({ ...newHabit, count: e.target.value });
  };

  const handleNewHabitRemindAtChange = (e) => {
    setNewHabit({ ...newHabit, remindAt: e.target.value });
  };

  const handleNewHabitReminderChange = () => {
    setNewHabit({ ...newHabit, reminder: !newHabit.reminder });
  };

  const handleSaveNewHabit = async () => {
    try {
      const { name, type, reminder, remindAt, count } = newHabit;

      if (!name || !type || typeof reminder !== 'boolean') {
        alert('Please fill in all fields');
        return;
      }

      if (type === 'COUNT' && !count) {
        alert('Please fill in the count');
        return;
      }

      if (reminder && !remindAt) {
        alert('Please fill in the remindAt');
        return;
      }

      const utcRemindAt = reminder
        ? `${new Date(`${formatDate(new Date())}T${remindAt}`)
            .getUTCHours()
            .toString()
            .padStart(2, '0')}:${new Date(
            `${formatDate(new Date())}T${remindAt}`
          )
            .getUTCMinutes()
            .toString()
            .padStart(2, '0')}`
        : null;

      const { result } = await request.post('/user/habit', {
        name,
        type,
        reminder,
        remindAt: utcRemindAt,
        count: type === 'COUNT' ? Number(count) : null,
      });

      if (result) {
        result.remindAt = result.reminder ? remindAt : '';

        setHabits([...habits, result]);
        handleCloseTypeDialog();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleUpdateHabitNameChange = (e) => {
    setEditingHabit({ ...editingHabit, name: e.target.value });
  };

  const handleUpdateHabitCountChange = (e) => {
    setEditingHabit({ ...editingHabit, count: e.target.value });
  };

  const handleUpdateHabitRemindAtChange = (e) => {
    setEditingHabit({ ...editingHabit, remindAt: e.target.value });
  };

  const handleUpdateHabitReminderChange = () => {
    setEditingHabit({ ...editingHabit, reminder: !editingHabit.reminder });
  };

  const handleSaveUpdateHabit = async (id) => {
    try {
      const utcRemindAt = editingHabit.reminder
        ? `${new Date(`${formatDate(new Date())}T${editingHabit.remindAt}`)
            .getUTCHours()
            .toString()
            .padStart(2, '0')}:${new Date(
            `${formatDate(new Date())}T${editingHabit.remindAt}`
          )
            .getUTCMinutes()
            .toString()
            .padStart(2, '0')}`
        : null;

      const { result } = await request.put(`/user/habit/${id}`, {
        ...editingHabit,
        count:
          editingHabit.type === 'COUNT' ? Number(editingHabit.count) : null,
        remindAt: utcRemindAt,
      });

      if (result) {
        result.remindAt = result.reminder ? editingHabit.remindAt : '';

        setHabits(
          habits.map((habit) => (habit.id === id ? { ...result } : habit))
        );
      }

      closeUpdateHabitDialog();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    (user.activated || user.programRegistered) && (
      <div>
        <div className='flex items-center justify-end mb-4'>
          <button
            className='bg-tertiary rounded-full size-8'
            onClick={handleAddHabit}
          >
            +
          </button>
        </div>
        <div className={`relative space-y-4`}>
          {habits.map((habit, i) => (
            <div
              key={i}
              className='w-full rounded-xl bg-secondary px-4'
            >
              <div className='flex items-center gap-2'>
                <div>
                  <CheckCircleIcon className='size-6 text-tertiary' />
                </div>
                <div
                  className='w-full flex items-center justify-between gap-2 py-4 cursor-pointer'
                  onClick={() => openUpdateHabitDialog(habit)}
                >
                  <div className={`text-xs text-white`}>{habit.name}</div>
                  {habit.reminder && (
                    <div className='text-xs text-white/50'>
                      {habit.remindAt}
                    </div>
                  )}
                </div>
                <button
                  className='text-tertiary'
                  onClick={() => handleDeleteHabit(habit.id)}
                >
                  <TrashIcon className='size-5' />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ADD HABIT DIALOG */}
        <dialog
          ref={typeDialogRef}
          className='w-full h-full max-w-lg max-h-full bg-transparent'
          onCancel={handleCloseTypeDialog}
        >
          <div
            className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 flex flex-col`}
          >
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-white font-bold'>Select Habit Type</h2>
              <button
                className='text-2xl text-white'
                onClick={handleCloseTypeDialog}
              >
                🅧
              </button>
            </div>
            {newHabit ? (
              <div className='flex flex-col gap-2 w-full'>
                <div className='text-white/50'>
                  {newHabit.type === 'COUNT' ? 'Counting Habit' : 'Check Habit'}
                </div>
                <div className='mb-4'>
                  <input
                    className='bg-tertiary rounded-full text-white px-3 py-2 w-full'
                    type='text'
                    value={newHabit.name}
                    autoFocus={true}
                    onChange={handleNewHabitNameChange}
                    placeholder='New habit'
                  />
                </div>
                {newHabit.type === 'COUNT' && (
                  <div className='text-white flex items-center justify-between mb-4'>
                    <label className='text-white font-semibold'>Count</label>
                    <select
                      name='count'
                      value={newHabit.count}
                      onChange={handleNewHabitCountChange}
                      className='rounded-full py-1 bg-tertiary  text-white w-[90px] text-center'
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <option
                          key={i}
                          value={i}
                        >
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className='flex items-center justify-between gap-2 mb-4'>
                  <label className='text-white font-semibold'>Reminder</label>
                  <div
                    onClick={handleNewHabitReminderChange}
                    className='relative inline-flex items-center cursor-pointer w-11 h-6'
                  >
                    <div
                      className={`absolute w-full h-full rounded-full transition-colors duration-200 ease-in-out flex items-center justify-around px-1 text-xs font-medium bg-black`}
                    ></div>
                    <div
                      className={`absolute size-5 rounded-full top-0.5 transition-all duration-200 ease-in-out ${
                        newHabit.reminder
                          ? 'translate-x-[22px] bg-accent'
                          : 'translate-x-0.5 bg-tertiary'
                      }`}
                    />
                  </div>
                </div>
                {newHabit.reminder && (
                  <div className='flex items-center justify-between gap-2 mb-4'>
                    <label className='text-white font-semibold'>
                      Remind me at
                    </label>
                    <input
                      type='time'
                      value={newHabit.remindAt}
                      onChange={handleNewHabitRemindAtChange}
                      className='rounded-full px-2 py-1 bg-tertiary  text-white'
                    />
                  </div>
                )}
                <button
                  className='text-black bg-accent rounded-full py-2 font-bold'
                  onClick={handleSaveNewHabit}
                >
                  Save
                </button>
              </div>
            ) : (
              <div className='flex flex-col gap-2'>
                <button
                  className='w-full bg-tertiary hover:bg-tertiary/80 text-white rounded-xl p-4 text-left'
                  onClick={() => handleSelectHabitType('COUNT')}
                >
                  Counting Habit
                  <div className='text-sm text-white/50'>
                    Track progress with numbers (e.g. glasses of water)
                  </div>
                </button>
                <button
                  className='w-full bg-tertiary hover:bg-tertiary/80 text-white rounded-xl p-4 text-left'
                  onClick={() => handleSelectHabitType('CHECK')}
                >
                  Check Habit
                  <div className='text-sm text-white/50'>
                    Simple check completion (e.g. daily workout)
                  </div>
                </button>
              </div>
            )}
          </div>
        </dialog>

        {/* UPDATE HABIT DIALOG */}
        <dialog
          ref={updateHabitRef}
          className='w-full h-full max-w-lg max-h-full bg-transparent'
          onCancel={closeUpdateHabitDialog}
        >
          <div
            className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 flex flex-col`}
          >
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-white font-bold'>Edit Habit</h2>
              <button
                className='text-2xl text-white'
                onClick={closeUpdateHabitDialog}
              >
                🅧
              </button>
            </div>
            {editingHabit && (
              <div className='flex flex-col gap-2 w-full'>
                <div className='text-white/50'>
                  {editingHabit.type === 'COUNT'
                    ? 'Counting Habit'
                    : 'Check Habit'}
                </div>
                <div className='mb-4'>
                  <input
                    className='bg-tertiary rounded-full text-white px-3 py-2 w-full'
                    type='text'
                    value={editingHabit.name}
                    autoFocus={true}
                    onChange={handleUpdateHabitNameChange}
                  />
                </div>
                {editingHabit.type === 'COUNT' && (
                  <div className='text-white flex items-center justify-between mb-4'>
                    <label className='text-white font-semibold'>Count</label>
                    <select
                      name='hours'
                      value={editingHabit.count}
                      onChange={handleUpdateHabitCountChange}
                      className='rounded-full py-1 bg-tertiary  text-white w-[90px] text-center'
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <option
                          key={i}
                          value={i}
                        >
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className='flex items-center justify-between gap-2 mb-4'>
                  <label className='text-white font-semibold'>Reminder</label>
                  <div
                    onClick={handleUpdateHabitReminderChange}
                    className='relative inline-flex items-center cursor-pointer w-11 h-6'
                  >
                    <div
                      className={`absolute w-full h-full rounded-full transition-colors duration-200 ease-in-out flex items-center justify-around px-1 text-xs font-medium bg-black`}
                    ></div>
                    <div
                      className={`absolute size-5 rounded-full top-0.5 transition-all duration-200 ease-in-out ${
                        editingHabit.reminder
                          ? 'translate-x-[22px] bg-accent'
                          : 'translate-x-0.5 bg-tertiary'
                      }`}
                    />
                  </div>
                </div>
                {editingHabit.reminder && (
                  <div className='flex items-center justify-between gap-2 mb-4'>
                    <label className='text-white font-semibold'>
                      Remind me at
                    </label>
                    <input
                      type='time'
                      value={editingHabit.remindAt}
                      onChange={handleUpdateHabitRemindAtChange}
                      className='rounded-full px-2 py-1 bg-tertiary  text-white'
                    />
                  </div>
                )}
                <button
                  className='text-black bg-accent rounded-full py-2 font-bold'
                  onClick={() => handleSaveUpdateHabit(editingHabit.id)}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </dialog>
      </div>
    )
  );
};

export default Habit;
